import { SelectOptionType } from '@octano/global-ui';
import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosResultDefaultError } from '../../../../../api/request';
import { AuthenticationError } from '../../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../components/info/DisplayError';
import Loading from '../../../../../components/info/Loading';
import { getPostulantAcademicHistory } from '../../../api/academicHistoryRequests';
import {
  PostulantInfo,
  StudentAcademicHistory,
  StudyPlanEnrollmentsInfo,
} from '../../../../StudentManagement/StudentFile/types';

export interface StudentAcademicHistoryLoaderProps {
  onError?: (code: string, onUnknown?: (() => void) | undefined) => void;
  children: (props: {
    postulant: PostulantInfo;
    studyPlanEnrollments: SelectOptionType[];
    studyPlanEnrollmentsInfo: StudyPlanEnrollmentsInfo[];
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  StudentAcademicHistory,
  AuthenticationError | AxiosResultDefaultError
>();

export const StudentAcademicHistoryLoaderConsumer = FetchConsumer;

export function useStudentAcademicHistory() {
  return useFetch();
}

export default function StudentAcademicHistoryLoader({
  onError,
  children,
}: StudentAcademicHistoryLoaderProps) {
  const { studentId } = useParams<{ studentId: string }>();
  const request = useCallback(() => {
    return getPostulantAcademicHistory(studentId);
  }, [studentId]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            onError && onError(error.code);
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return <Loading insideCard />;
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({
            postulant: data.postulant,
            studyPlanEnrollments: data.studyPlanEnrollments.map(
              (studyPlanEnrollment: StudyPlanEnrollmentsInfo) => ({
                label: studyPlanEnrollment.name.toUpperCase(),
                value: studyPlanEnrollment.studyPlanEnrollmentId,
              }),
            ),
            studyPlanEnrollmentsInfo: data.studyPlanEnrollments,
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
